import { ChangeDetectionStrategy, Component, ElementRef, computed, inject, input, output, signal } from '@angular/core';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { ScoIconButtonModule, ScoSearchModule } from '@schueco/schueco-ui-components';
import { TranslateModule } from '@ngx-translate/core';

export interface CategoryTreeNode {
  id: number;
  title: string;
  subLinks?: CategoryTreeNode[];
}

const PANEL_OFFSET_PX = 5;
const PANEL_MARGIN_BOTTOM_PX = 5;

interface CategoryTreeRow {
  node: CategoryTreeNode;
  level: number;
  hasChildren: boolean;
  expanded: boolean;
}

@Component({
  standalone: true,
  selector: 'dc-category-tree-dropdown',
  imports: [OverlayModule, ScoIconButtonModule, ScoSearchModule, TranslateModule],
  templateUrl: './category-tree-dropdown.component.html',
  styleUrl: './category-tree-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CategoryTreeDropdownComponent {
  private host = inject<ElementRef<HTMLElement>>(ElementRef);

  nodes = input<CategoryTreeNode[]>([]);
  selectedId = input<number | null>(null);
  // Title of the active search context (category, favorites or collection); null means no context
  contextTitle = input<string | null>(null);

  nodeSelected = output<CategoryTreeNode>();
  allCategoriesSelected = output<void>();
  selectionCleared = output<void>();
  opened = output<void>();

  isOpen = signal(false);
  panelMaxHeight = signal<number | null>(null);
  filterText = signal('');
  private expandedIds = signal<ReadonlySet<number>>(new Set());

  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: PANEL_OFFSET_PX },
  ];

  hasSelection = computed(() => !!this.contextTitle());

  rows = computed<CategoryTreeRow[]>(() => {
    const expandedIds = this.expandedIds();
    const query = this.filterText().trim().toLowerCase();
    const visibleNodes = query ? this.filterTree(this.nodes(), query) : this.nodes();
    const rows: CategoryTreeRow[] = [];
    const walk = (nodes: CategoryTreeNode[], level: number) => {
      for (const node of nodes) {
        const hasChildren = !!node.subLinks?.length;
        const expanded = hasChildren && expandedIds.has(node.id);
        rows.push({ node, level, hasChildren, expanded });
        if (expanded) {
          walk(node.subLinks!, level + 1);
        }
      }
    };
    walk(visibleNodes, 0);
    return rows;
  });

  isSelected(node: CategoryTreeNode): boolean {
    return node.id === this.selectedId();
  }

  toggleOpen(): void {
    if (this.isOpen()) {
      this.close();
      return;
    }
    this.filterText.set('');
    this.revealSelected();
    const triggerBottom = this.host.nativeElement.getBoundingClientRect().bottom;
    this.panelMaxHeight.set(Math.max(window.innerHeight - triggerBottom - PANEL_OFFSET_PX - PANEL_MARGIN_BOTTOM_PX, 0));
    this.isOpen.set(true);
    this.opened.emit();
  }

  close(): void {
    this.isOpen.set(false);
  }

  onOutsideClick(event: MouseEvent): void {
    if (!this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }

  onFilterInput(value: string): void {
    this.filterText.set(value);
    const query = value.trim().toLowerCase();
    if (!query) {
      this.revealSelected();
      return;
    }
    // Expand every node that has a matching descendant so matches are visible
    const ids = new Set<number>();
    this.collectExpandIds(this.nodes(), query, ids);
    this.expandedIds.set(ids);
  }

  clearFilter(): void {
    this.onFilterInput('');
  }

  toggleExpanded(node: CategoryTreeNode, event: Event): void {
    event.stopPropagation();
    const next = new Set(this.expandedIds());
    if (!next.delete(node.id)) {
      next.add(node.id);
    }
    this.expandedIds.set(next);
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    this.selectionCleared.emit();
    this.close();
  }

  select(node: CategoryTreeNode): void {
    this.nodeSelected.emit(node);
    this.close();
  }

  selectAll(): void {
    this.allCategoriesSelected.emit();
    this.close();
  }

  private revealSelected(): void {
    const id = this.selectedId();
    const path = id === null ? [] : this.findPath(this.nodes(), id);
    this.expandedIds.set(new Set(path.slice(0, -1).map((node) => node.id)));
  }

  // Keeps nodes that match or have matching descendants; matching nodes without matching children stay as-is
  private filterTree(nodes: CategoryTreeNode[], query: string): CategoryTreeNode[] {
    return nodes.flatMap((node) => {
      const children = node.subLinks?.length ? this.filterTree(node.subLinks, query) : [];
      if (children.length) {
        return [{ ...node, subLinks: children }];
      }
      return node.title.toLowerCase().includes(query) ? [node] : [];
    });
  }

  // Returns true if the subtree contains a match; collects ids of nodes with a matching descendant
  private collectExpandIds(nodes: CategoryTreeNode[], query: string, ids: Set<number>): boolean {
    let found = false;
    for (const node of nodes) {
      const childMatch = node.subLinks?.length ? this.collectExpandIds(node.subLinks, query, ids) : false;
      if (childMatch) {
        ids.add(node.id);
      }
      found = found || childMatch || node.title.toLowerCase().includes(query);
    }
    return found;
  }

  private findPath(nodes: CategoryTreeNode[], id: number): CategoryTreeNode[] {
    for (const node of nodes) {
      if (node.id === id) {
        return [node];
      }
      const childPath = node.subLinks?.length ? this.findPath(node.subLinks, id) : [];
      if (childPath.length) {
        return [node, ...childPath];
      }
    }
    return [];
  }
}
